import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Cloud, MapPin, Droplets, Wind } from "lucide-react";

interface WeatherData {
  temp: number;
  humidity: number;
  windSpeed: number;
  description: string;
}

const BA_FACTS = [
  "Buenos Aires is known as the 'Paris of South America' for its European architecture and culture.",
  "The city has the widest avenue in the world - Avenida 9 de Julio.",
  "Buenos Aires is the birthplace of Tango, a passionate dance and music genre.",
  "The city is home to Diego Maradona, one of the greatest football players of all time.",
  "La Boca neighborhood features colorful buildings and is a UNESCO World Heritage Site.",
  "Buenos Aires has more bookstores per capita than any other city in the world.",
  "The city's cemetery, La Recoleta, is so famous it's called the 'City of the Dead'.",
  "Argentinian beef is world-renowned, and BBQ (asado) is a cultural tradition.",
  "The Teatro Colón is one of the most prestigious opera houses in the world.",
  "Buenos Aires never sleeps - nightlife typically starts after midnight.",
];

export function BuenosAiresData() {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [fact, setFact] = useState<string>(BA_FACTS[0]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const response = await fetch(
          "https://api.open-meteo.com/v1/forecast?latitude=-34.6037&longitude=-58.3816&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m&temperature_unit=celsius"
        );
        const data = await response.json();
        const current = data.current;

        setWeather({
          temp: Math.round(current.temperature_2m),
          humidity: current.relative_humidity_2m,
          windSpeed: Math.round(current.wind_speed_10m),
          description: getWeatherDescription(current.weather_code),
        });
      } catch (error) {
        console.error("Error fetching weather:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
    const interval = setInterval(() => {
      setFact(BA_FACTS[Math.floor(Math.random() * BA_FACTS.length)]);
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  function getWeatherDescription(code: number): string {
    const descriptions: Record<number, string> = {
      0: "Clear",
      1: "Mostly Clear",
      2: "Partly Cloudy",
      3: "Overcast",
      45: "Foggy",
      48: "Foggy",
      51: "Light Drizzle",
      61: "Rainy",
      71: "Snowy",
      80: "Heavy Rain",
      99: "Thunderstorm",
    };
    return descriptions[code] || "Unknown";
  }

  return (
    <div className="space-y-4">
      <Card className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-slate-900 dark:to-slate-800 border-0">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="w-5 h-5 text-blue-600" />
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
            Buenos Aires
          </h2>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-3">
            <div className="h-12 bg-slate-200 dark:bg-slate-700 rounded"></div>
            <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded"></div>
          </div>
        ) : weather ? (
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <Cloud className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Temperature</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {weather.temp}°C
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Droplets className="w-8 h-8 text-cyan-500" />
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Humidity</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {weather.humidity}%
                </p>
              </div>
            </div>

            <div className="col-span-2 flex items-center gap-3">
              <Wind className="w-8 h-8 text-teal-500" />
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Wind Speed</p>
                <p className="text-lg font-semibold text-slate-900 dark:text-white">
                  {weather.windSpeed} km/h - {weather.description}
                </p>
              </div>
            </div>
          </div>
        ) : null}
      </Card>

      <Card className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-slate-900 dark:to-slate-800 border-0 min-h-24 flex items-center">
        <p className="text-lg text-slate-800 dark:text-slate-100 italic leading-relaxed animate-in fade-in duration-500">
          {fact}
        </p>
      </Card>
    </div>
  );
}
